import type { CSSProperties } from 'react';
import { useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { AVATAR_COLOURS } from 'definitions';
import { useAuth } from 'hooks/useAuth';
import { cn } from 'helpers/cn';
import { AVATAR_HUES, avatarColour, avatarFill, isAvatarColour, type AvatarColour } from 'helpers/user-identity';
import { UserAvatar } from '@/components/UserAvatar';
import { Panel } from '../../ConsolePanel';

/** What is being saved: a colour, or null for automatic. Undefined while nothing is. */
type Pending = AvatarColour | null | undefined;

const SWATCH =
  'bg-(--avatar-fill) text-white rounded-pill flex size-8 items-center justify-center transition-transform active:scale-[0.94] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none disabled:opacity-60';

const hueStyle = (colour: AvatarColour) => ({ '--avatar-fill': avatarFill(AVATAR_HUES[colour]) }) as CSSProperties;

/**
 * The colour of your initials badge, as everyone else sees it: in the header, the team
 * lists, the experiment library and the activity log.
 *
 * Saved on click, like a toggle. The badge on the left previews the choice, so a save
 * button would only add a step. Automatic is listed first and keeps the colour your id
 * gives you, so going back is always one click.
 */
export function BadgeColour() {
  const { user, updateUser } = useAuth();
  const [pending, setPending] = useState<Pending>(undefined);
  if (!user) return null;

  const saved = isAvatarColour(user.avatarColour) ? user.avatarColour : null;
  const selected = pending !== undefined ? pending : saved;
  const automatic = avatarColour({ id: user.id });

  const choose = async (next: AvatarColour | null) => {
    if (next === selected) return;
    setPending(next);
    try {
      await updateUser({ avatarColour: next });
    } catch {
      toast.error('Your badge colour could not be saved. Please try again.');
    } finally {
      setPending(undefined);
    }
  };

  return (
    <Panel
      title="Badge colour"
      description="How your initials appear to your team, wherever your name is shown."
      tourAnchor="settings-account-badge"
    >
      <div className="flex flex-wrap items-center gap-4">
        <UserAvatar user={{ ...user, avatar: null, avatarColour: selected }} size="lg" />

        <div role="radiogroup" aria-label="Badge colour" className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            role="radio"
            aria-checked={selected === null}
            disabled={pending !== undefined}
            onClick={() => choose(null)}
            style={hueStyle(automatic)}
            className={cn(
              SWATCH,
              'w-auto px-3 text-meta font-medium',
              selected === null && 'ring-accent ring-2 ring-offset-2 ring-offset-background',
            )}
          >
            Automatic
          </button>

          {AVATAR_COLOURS.map((colour) => (
            <button
              key={colour}
              type="button"
              role="radio"
              aria-checked={selected === colour}
              aria-label={colour}
              title={colour}
              disabled={pending !== undefined}
              onClick={() => choose(colour)}
              style={hueStyle(colour)}
              className={cn(SWATCH, selected === colour && 'ring-accent ring-2 ring-offset-2 ring-offset-background')}
            >
              {selected === colour && <Check className="size-4" strokeWidth={2.25} aria-hidden="true" />}
            </button>
          ))}
        </div>
      </div>

      {!!user.avatar && (
        <p className="supporting mt-3 mb-0">
          You have a photo, so it is shown instead. The colour is used if you remove it.
        </p>
      )}
    </Panel>
  );
}
